import { auth } from "../firebase";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5000/api";

export interface PaymentIntentRequest {
  items: Array<{
    productId: string;
    name: string;
    price: number;
    quantity: number;
    image?: string;
  }>;
  totalAmount: number; // Total amount including tax and shipping
  customerEmail: string;
  customerName: string;
  shippingAddress: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  billingAddress: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
}

export interface PaymentIntentResponse {
  success: boolean;
  clientSecret: string;
  orderId: string;
  paymentIntentId: string;
}

export interface PaymentConfirmationRequest {
  paymentIntentId: string;
  orderId: string;
}

export interface PaymentConfirmationResponse {
  success: boolean;
  status: string;
  orderId: string;
  paymentIntentId?: string;
}

export const paymentService = {
  async createPaymentIntent(
    data: PaymentIntentRequest
  ): Promise<PaymentIntentResponse> {
    try {
      // Allow guest checkout - get token only if user is logged in
      const user = auth.currentUser;
      const headers: HeadersInit = {
        "Content-Type": "application/json",
      };

      // Add authorization header only if user is authenticated
      if (user) {
        const token = await user.getIdToken();
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(
        `${API_BASE_URL}/payments/create-payment-intent`,
        {
          method: "POST",
          headers,
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to create payment intent");
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error("Error creating payment intent:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      throw new Error(`Failed to create payment intent: ${errorMessage}`);
    }
  },

  async confirmPayment(
    data: PaymentConfirmationRequest
  ): Promise<PaymentConfirmationResponse> {
    try {
      // Allow guest checkout - get token only if user is logged in
      const user = auth.currentUser;
      const headers: HeadersInit = {
        "Content-Type": "application/json",
      };

      // Add authorization header only if user is authenticated
      if (user) {
        const token = await user.getIdToken();
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/payments/confirm-payment`, {
        method: "POST",
        headers,
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to confirm payment");
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error("Error confirming payment:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      throw new Error(`Failed to confirm payment: ${errorMessage}`);
    }
  },

  async getPaymentStatus(paymentIntentId: string) {
    try {
      // Allow guest checkout - get token only if user is logged in
      const user = auth.currentUser;
      const headers: HeadersInit = {};

      // Add authorization header only if user is authenticated
      if (user) {
        const token = await user.getIdToken();
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(
        `${API_BASE_URL}/payments/status/${paymentIntentId}`,
        {
          method: "GET",
          headers,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to get payment status");
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error("Error getting payment status:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      throw new Error(`Failed to get payment status: ${errorMessage}`);
    }
  },
};
